// Boost Blenz Admin — client-side stale-while-revalidate cache.
//
// Why this exists: every view hook used to be useState(null) + fetch-on-mount, so
// switching tabs threw the data away and re-hit the network every time — a spinner
// on every navigation even for a page you saw seconds ago. This keeps each
// resource's last response in a module-level cache so a revisit paints INSTANTLY,
// then quietly revalidates in the background. The server side is already cache-
// backed (cron-warmed KV / D1), so revalidation is cheap; this just stops the UI
// from discarding what it already had.
//
// Guarantees:
//   • Instant paint on revisit — cached data is the initial state, no spinner.
//   • Background revalidate — always refreshes after paint (unless very recent).
//   • In-flight dedupe — concurrent mounts on the same key share one request.
//   • Cross-component sync — updating a key updates every mounted reader of it.
//   • Never shows stale on a hard refresh — refresh(fresh) bypasses the dedupe
//     window and forces a network round-trip.

import React from "react";

type Entry = { data: any; at: number; err?: boolean };
const store = new Map<string, Entry>();
const inflight = new Map<string, Promise<any>>();
const subs = new Map<string, Set<() => void>>();

// ---- sessionStorage persistence -------------------------------------------
// The in-memory store gives instant paint when NAVIGATING between pages, but a
// hard reload (or reopening the tab) used to start empty → every page re-hit the
// network. Persisting each entry to sessionStorage lets a reload paint instantly
// from the last response, then revalidate in the background (entries are always
// >12s old after a reload, so run(false) refreshes them immediately). Per-tab and
// cleared on close, so financial data never lingers on disk. Bumped PERSIST_V on
// any shape change so a deploy can't paint an incompatible cached shape.
const PERSIST_V = "v1";
const PKEY = (key: string) => `bbcache:${PERSIST_V}:${key}`;
const PERSIST_MAX = 180_000; // don't persist a single entry larger than ~180KB
const canPersist = typeof window !== "undefined" && !!window.sessionStorage;

function persist(key: string, entry: Entry) {
  if (!canPersist) return;
  try {
    const s = JSON.stringify({ data: entry.data, at: entry.at });
    if (s.length <= PERSIST_MAX) window.sessionStorage.setItem(PKEY(key), s);
  } catch { /* quota / serialization — cache still works in-memory */ }
}

// Hydrate the in-memory store from sessionStorage once at module load.
if (canPersist) {
  try {
    const prefix = `bbcache:${PERSIST_V}:`;
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const k = window.sessionStorage.key(i);
      if (!k || !k.startsWith(prefix)) continue;
      const raw = window.sessionStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.data !== undefined) store.set(k.slice(prefix.length), { data: parsed.data, at: parsed.at || 0 });
    }
    // Drop any entries from an older cache version so they can't accumulate.
    const stale: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith("bbcache:") && !k.startsWith(prefix)) stale.push(k);
    }
    for (const k of stale) window.sessionStorage.removeItem(k);
  } catch { /* corrupt storage — ignore, start empty */ }
}

// How recent a cached entry has to be for a background revalidate to be skipped on
// mount. Flipping quickly between two tabs then shouldn't re-fetch each time; a
// hard Refresh always bypasses this.
const REVALIDATE_AFTER_MS = 12_000;

function notify(key: string) {
  const s = subs.get(key);
  if (s) for (const fn of s) fn();
}

// Read/imperatively update a cached entry from outside a component (used by hooks
// that mutate a resource — e.g. saving an expense returns the fresh list).
export function cacheGet<T = any>(key: string): T | undefined {
  return store.get(key)?.data as T | undefined;
}
export function cacheSet(key: string, data: any) {
  const entry = { data, at: Date.now() };
  store.set(key, entry);
  persist(key, entry);
  notify(key);
}
export function cacheInvalidate(key: string) {
  store.delete(key);
  if (canPersist) { try { window.sessionStorage.removeItem(PKEY(key)); } catch { /* ignore */ } }
  notify(key);
}

// Run `fetcher` for `key`, deduping concurrent callers and storing the result.
async function load(key: string, fetcher: () => Promise<any>): Promise<any> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = (async () => {
    try {
      const data = await fetcher();
      // A fetcher may return null/undefined to mean "keep what we had" (e.g. a
      // network blip) — only overwrite the cache on a real value.
      if (data !== null && data !== undefined) { const entry = { data, at: Date.now() }; store.set(key, entry); persist(key, entry); }
      return data;
    } catch {
      const prev = store.get(key);
      if (prev) store.set(key, { ...prev, err: true });
      return undefined;
    } finally {
      inflight.delete(key);
      notify(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

export type CachedResource<T> = {
  data: T | null;
  loading: boolean;      // true only on a cold miss (nothing cached yet)
  revalidating: boolean; // a background refresh is in flight over existing data
  error: boolean;
  refresh: () => void;   // force a fresh network round-trip (bypasses the dedupe window)
  set: (data: T) => void; // imperatively update the cache (post-mutation)
};

// Subscribe a component to a cached resource. `fetcher` should return the parsed
// value, or null/undefined to leave the cache untouched. Pass `enabled: false` to
// hold off (e.g. a custom date range that isn't complete yet).
export function useCachedResource<T = any>(
  key: string | null,
  fetcher: (opts: { fresh: boolean }) => Promise<T | null>,
  opts: { enabled?: boolean } = {},
): CachedResource<T> {
  const enabled = opts.enabled !== false && key != null;
  const fetcherRef = React.useRef(fetcher);
  fetcherRef.current = fetcher;

  // Re-render this component when its key's entry changes.
  const [, force] = React.useReducer((n) => n + 1, 0);
  React.useEffect(() => {
    if (!key) return;
    let set = subs.get(key);
    if (!set) subs.set(key, (set = new Set()));
    set.add(force);
    return () => { set!.delete(force); };
  }, [key]);

  const run = React.useCallback((fresh: boolean) => {
    if (!key) return;
    const cached = store.get(key);
    // Skip a redundant background refresh if we just fetched — unless forced.
    if (!fresh && cached && !cached.err && Date.now() - cached.at < REVALIDATE_AFTER_MS) return;
    void load(key, () => fetcherRef.current({ fresh }));
    notify(key); // reflect "revalidating" immediately
  }, [key]);

  React.useEffect(() => {
    if (!enabled) return;
    run(false);
  }, [enabled, run]);

  const entry = key ? store.get(key) : undefined;
  const hasData = entry !== undefined && entry.data !== undefined;
  return {
    data: hasData ? (entry!.data as T) : null,
    loading: enabled && !hasData && !(entry?.err),
    revalidating: !!(key && inflight.has(key)),
    error: !!entry?.err && !hasData,
    refresh: () => run(true),
    set: (data: T) => cacheSet(key!, data),
  };
}

// Convenience wrapper for the common "GET a JSON endpoint" case. `fresh` appends
// the cache-busting query param the Workers use (?fresh=1).
export function useCachedJson<T = any>(
  url: string | null,
  opts: { enabled?: boolean; freshParam?: string } = {},
): CachedResource<T> {
  const freshParam = opts.freshParam ?? "fresh";
  return useCachedResource<T>(
    url,
    async ({ fresh }) => {
      const u = fresh ? url! + (url!.includes("?") ? "&" : "?") + `${freshParam}=1` : url!;
      const r = await fetch(u);
      if (!r.ok) throw new Error(String(r.status));
      return (await r.json()) as T;
    },
    { enabled: opts.enabled },
  );
}
